/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useState,
  useCallback,
  forwardRef,
  useFocusOut,
} from '@web-stories-wp/react';
import styled from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  themeHelpers,
  Icons,
} from '@web-stories-wp/design-system';
import { STORY_ANIMATION_STATE } from '@web-stories-wp/animation';
/**
 * Internal dependencies
 */
import { PageSizePropType } from '../../../../types';
import { PreviewPage, PreviewErrorBoundary } from '../../../previewPage';
import { focusStyle } from '../../../panels/shared';
import { PreviewTemplatePoster } from '@components/previewTemplatePoster/PreviewTemplatePoster';

const PageTemplateWrapper = styled.div`
  position: absolute;
  top: 0;
  height: ${({ pageSize }) => pageSize.containerHeight}px;
  width: ${({ pageSize }) => pageSize.width}px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  opacity: ${({ isDragging }) => (isDragging ? 0.48 : 1)};
  transform: ${({ translateX, translateY }) =>
    `translateX(${translateX}px) translateY(${translateY}px)`};

  ${({ isHighlighted }) => isHighlighted && themeHelpers.focusCSS};
  ${focusStyle};
`;
PageTemplateWrapper.propTypes = {
  pageSize: PageSizePropType.isRequired,
  translateY: PropTypes.number.isRequired,
  translateX: PropTypes.number.isRequired,
};

const PreviewPageWrapper = styled.div`
  position: relative;
  height: ${({ pageSize }) => pageSize.containerHeight}px;
  width: ${({ pageSize }) => pageSize.width}px;
  z-index: 0;
  background-color: ${({ theme }) => theme.colors.interactiveBg.secondary};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  overflow: hidden;
`;
PreviewPageWrapper.propTypes = {
  pageSize: PageSizePropType.isRequired,
};

const ButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  padding: 8px;
`;

const PinnedIndicator = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e4e5e6;
  pointer-events: none;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ReorderDragSurface = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: transparent;
  cursor: inherit;
  user-select: none;
  touch-action: none;
`;

const PageTemplateTitle = styled.div`
  position: absolute;
  bottom: 0;
  background-color: rgba(20, 21, 22, 0.8);
  border-radius: ${({ theme }) => theme.borders.radius.small};
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};

  padding: 12px;
  color: #e4e5e6;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  width: 100%;
  align-self: flex-end;
  overflow: hidden;
  z-index: 3;
`;

const PageTemplateFolderPath = styled.div`
  margin-top: 2px;
  color: #c2c6c7;
  font-size: 9px;
  font-weight: 400;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

PageTemplateTitle.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

function PageTemplate(
  {
    page,
    pageSize,
    translateY,
    translateX,
    isActive,
    handleDelete,
    renderActions,
    isReorderable,
    isDragging,
    onReorderPointerDown,
    ...rest
  },
  ref
) {
  const [isHover, setIsHover] = useState(false);
  const isActivePage = isHover || isActive;

  useFocusOut(ref, () => setIsHover(false), []);

  const { highlightedTemplate } = rest;

  const handleSetHoverActive = useCallback(() => setIsHover(true), []);

  const handleSetHoverFalse = useCallback(() => {
    setIsHover(false);
  }, []);

  return (
    <PageTemplateWrapper
      pageSize={pageSize}
      role="listitem"
      ref={ref}
      // Needed for custom keyboard navigation implementation.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onMouseEnter={handleSetHoverActive}
      onMouseLeave={handleSetHoverFalse}
      aria-label={
        page.isPinned
          ? `${page.title || __('Template', 'web-stories')}, ${__(
              'pinned',
              'web-stories'
            )}${
              page.folderPath?.length
                ? `, ${page.folderPath.map(({ name }) => name).join(' / ')}`
                : ''
            }`
          : `${page.title || ''}${
              page.folderPath?.length
                ? `, ${page.folderPath.map(({ name }) => name).join(' / ')}`
                : ''
            }`
      }
      data-page-template-id={page.id}
      translateY={translateY}
      translateX={translateX}
      isHighlighted={page.id === highlightedTemplate}
      isDragging={isDragging}
      {...rest}
    >
      <PreviewPageWrapper pageSize={pageSize}>
        <PreviewErrorBoundary>
          {page.posterUrl ? (
            <PreviewTemplatePoster posterUrl={page.posterUrl} />
          ) : (
            <PreviewPage
              pageSize={pageSize}
              page={page}
              animationState={
                isActivePage
                  ? STORY_ANIMATION_STATE.PLAYING
                  : STORY_ANIMATION_STATE.RESET
              }
            />
          )}
        </PreviewErrorBoundary>
        {isReorderable && (
          <ReorderDragSurface
            aria-hidden="true"
            onPointerDown={onReorderPointerDown}
          />
        )}
        {page.isPinned && (
          <PinnedIndicator aria-hidden="true">
            <Icons.Pin />
          </PinnedIndicator>
        )}
        {isActivePage && renderActions && (
          <ButtonWrapper>{renderActions(page)}</ButtonWrapper>
        )}
        {isActivePage && !renderActions && handleDelete && (
          <ButtonWrapper>
            <Button
              variant={BUTTON_VARIANTS.CIRCLE}
              type={BUTTON_TYPES.SECONDARY}
              size={BUTTON_SIZES.SMALL}
              onClick={(e) => handleDelete(page, e)}
              aria-label={__('Delete Page Template', 'web-stories')}
            >
              <Icons.Trash />
            </Button>
          </ButtonWrapper>
        )}
      </PreviewPageWrapper>

      {page.title && (
        <PageTemplateTitle isActive={isActivePage}>
          {page.title}
          {page.folderPath?.length > 0 && (
            <PageTemplateFolderPath>
              {page.folderPath.map(({ name }) => name).join(' / ')}
            </PageTemplateFolderPath>
          )}
        </PageTemplateTitle>
      )}
    </PageTemplateWrapper>
  );
}

const PageTemplateWithRef = forwardRef(PageTemplate);

PageTemplate.propTypes = {
  isActive: PropTypes.bool,
  page: PropTypes.object.isRequired,
  pageSize: PageSizePropType.isRequired,
  translateY: PropTypes.number.isRequired,
  translateX: PropTypes.number.isRequired,
  handleDelete: PropTypes.func,
  renderActions: PropTypes.func,
  isReorderable: PropTypes.bool,
  isDragging: PropTypes.bool,
  onReorderPointerDown: PropTypes.func,
};

PageTemplate.displayName = 'PageTemplate';

export default PageTemplateWithRef;
